import { c as _c } from "react/compiler-runtime";
import { Box } from "@mui/material";
import React, { useMemo } from "react";
import { ArgumentType } from "#/entities/argument/model/argumentObject";
import { useBinaryTreePositioning } from "#/entities/dataStructures/binaryTree/hooks/useBinaryTreePositioning";
import { BinaryNode } from "#/entities/dataStructures/binaryTree/ui/BinaryNode";
import { GraphEdge } from "#/entities/dataStructures/graph/ui/GraphEdge";
import { GraphNode } from "#/entities/dataStructures/graph/ui/GraphNode";
import { LinkedListNode } from "#/entities/dataStructures/linkedList/ui/LinkedListNode";
import { selectMinXOffset, type TreeData } from "#/entities/dataStructures/node/model/nodeSlice";
import { useAppSelector } from "#/store/hooks";
const nodeComponentMap = {
  [ArgumentType.LINKED_LIST]: LinkedListNode,
  [ArgumentType.BINARY_TREE]: BinaryNode,
  [ArgumentType.GRAPH]: GraphNode
};
type NodesViewProps = {
  treeName: string;
  data: TreeData;
  style?: React.CSSProperties;
  sx?: React.CSSProperties;
  horizontalAlign?: "start" | "center";
};
const NODE_WIDTH = 42;
export const NodesView: React.FC<NodesViewProps> = t0 => {
  const $ = _c(30);
  const {
    treeName,
    data,
    style,
    sx,
    horizontalAlign: t1
  } = t0;
  const horizontalAlign = t1 === undefined ? "start" : t1;
  const adjustXOffset = data.type === ArgumentType.LINKED_LIST;
  const offset = useAppSelector(selectMinXOffset(treeName, adjustXOffset)) ?? 0;
  useBinaryTreePositioning(treeName, data);
  const Node = nodeComponentMap[data.type];
  style?.left;
  let t2;
  if ($[0] !== data.nodes.entities || $[1] !== data.type || $[2] !== horizontalAlign || $[3] !== offset || $[4] !== style?.left) {
    bb0: {
      const baseLeft = (Number(style?.left) || 0) - offset;
      if (horizontalAlign !== "center" || data.type !== ArgumentType.BINARY_TREE) {
        t2 = baseLeft;
        break bb0;
      }
      const nodes = Object.values(data.nodes.entities).filter(_temp);
      if (nodes.length === 0) {
        t2 = baseLeft;
        break bb0;
      }
      const minX = Math.min(...nodes.map(_temp2));
      const maxX = Math.max(...nodes.map(_temp3));
      const treeCenterX = (minX + maxX + NODE_WIDTH) / 2;
      if (baseLeft === 0) {
        t2 = `calc(50% - ${treeCenterX}px)`;
        break bb0;
      }
      const leftSign = baseLeft >= 0 ? "+" : "-";
      let t3;
      if ($[6] !== baseLeft) {
        t3 = Math.abs(baseLeft);
        $[6] = baseLeft;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t2 = `calc(50% ${leftSign} ${t3}px - ${treeCenterX}px)`;
    }
    $[0] = data.nodes.entities;
    $[1] = data.type;
    $[2] = horizontalAlign;
    $[3] = offset;
    $[4] = style?.left;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const left = t2;
  let t3;
  if ($[8] !== sx) {
    t3 = {
      position: "absolute",
      width: "100%",
      height: "100%",
      ...sx
    };
    $[8] = sx;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== left || $[11] !== style) {
    t4 = {
      ...style,
      left,
      transition: "left 0.05s ease-in-out"
    };
    $[10] = left;
    $[11] = style;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== data.edges.entities) {
    t5 = Object.values(data.edges.entities);
    $[13] = data.edges.entities;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5 || $[16] !== treeName) {
    t6 = t5.map(edge => edge && <GraphEdge key={edge.id} treeName={treeName} {...edge} />);
    $[15] = t5;
    $[16] = treeName;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== data.nodes.entities) {
    t7 = Object.values(data.nodes.entities);
    $[18] = data.nodes.entities;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== Node || $[21] !== data.type || $[22] !== t7 || $[23] !== treeName) {
    t8 = t7.map(node_2 => node_2 && <Node key={node_2.id} treeName={treeName} type={data.type} {...node_2} />);
    $[20] = Node;
    $[21] = data.type;
    $[22] = t7;
    $[23] = treeName;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t3 || $[26] !== t4 || $[27] !== t6 || $[28] !== t8) {
    t9 = <Box sx={t3} style={t4}>{t6}{t8}</Box>;
    $[25] = t3;
    $[26] = t4;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
function _temp(node) {
  return node !== undefined;
}
function _temp2(node_0) {
  return node_0.x;
}
function _temp3(node_1) {
  return node_1.x;
}